import {
  LuBriefcase,
  LuHeart,
  LuMail,
  LuMapPin,
  LuMessageSquare,
  LuPhone,
  LuTarget,
  LuTrendingUp,
  LuUsers,
} from 'react-icons/lu'

/** Category display name → skill chips (mockup SUB map). */
export const ADVISE_CATEGORY_SKILLS: Record<string, string[]> = {
  Funding: [
    'Fundraise strategy',
    'Late stage venture',
    'Seed & pre-seed',
    'Investor intros',
    'Pitch & narrative',
    'Valuation & terms',
  ],
  'Customer acquisition': [
    'Paid acquisition',
    'Growth loops',
    'Lifecycle & retention',
    'SEO & content',
    'Sales-led growth',
    'Community',
  ],
  Product: ['Product strategy', 'Roadmapping', 'Finding PMF', 'UX & design', 'Discovery', 'Analytics'],
  'Engineering & tech': [
    'Architecture',
    'Scaling infra',
    'Hiring engineers',
    'Technical due diligence',
    'Security',
    'AI & ML',
  ],
  'Team & hiring': [
    'Recruiting',
    'Org design',
    'Comp & equity',
    'Culture',
    'First 10 hires',
    'Leadership coaching',
  ],
  'Ops & finance': [
    'Financial modeling',
    'Metrics & KPIs',
    'Fundraising ops',
    'Legal & compliance',
    'Board management',
    'Runway planning',
  ],
  'Marketing & brand': [
    'Positioning',
    'Brand strategy',
    'Content',
    'PR',
    'Demand gen',
    'Performance marketing',
  ],
  Legal: ['Incorporation', 'Cap table', 'Contracts', 'IP & trademark', 'Fundraising docs', 'Compliance'],
  Leadership: [
    'Founder coaching',
    'Scaling teams',
    'Company strategy',
    'Fundraising narrative',
    'Board & investors',
    'Decision making',
  ],
}

export const ADVISE_CATEGORY_NAMES = Object.keys(ADVISE_CATEGORY_SKILLS)

/** Max category blocks + skills per block (mockup). */
export const MAX_CATEGORY_BLOCKS = 3
export const MAX_SKILLS_PER_CATEGORY = 3
export const HEADLINE_MAX = 72
export const NOTE_MAX = 240

export const WARM_INTRO_OPTS = [
  {
    label: 'Investors',
    desc: 'Angels, seed & VC',
    palette: 'yellow',
    icon: LuBriefcase,
  },
  {
    label: 'Customers',
    desc: 'Buyers & partners',
    palette: 'teal',
    icon: LuTarget,
  },
  {
    label: 'Talent',
    desc: 'Operators & hires',
    palette: 'purple',
    icon: LuUsers,
  },
] as const

export const COMP_OPTS = [
  {
    key: 'equity',
    label: 'Equity',
    desc: 'An advisory grant that vests over time.',
    palette: 'green',
    icon: LuTrendingUp,
  },
  {
    key: 'cash',
    label: 'Cash per session',
    desc: 'A set rate for calls & office hours.',
    palette: 'orange',
    icon: LuBriefcase,
  },
  {
    key: 'none',
    label: 'Pro bono',
    desc: 'Happy to help select Founders for free.',
    palette: 'pink',
    icon: LuHeart,
  },
] as const

/** How founders engage — mockup §6. Maps to rough hours for availabilityHoursPerMonth. */
export const ENGAGE_OPTS = [
  {
    label: 'Scheduled calls',
    desc: 'Zoom & phone',
    palette: 'teal',
    hours: 5,
    icon: LuPhone,
  },
  {
    label: 'Email',
    desc: 'Written, async',
    palette: 'yellow',
    hours: 2,
    icon: LuMail,
  },
  {
    label: 'Chat / DMs',
    desc: 'Quick messages',
    palette: 'purple',
    hours: 1,
    icon: LuMessageSquare,
  },
  {
    label: 'In person',
    desc: 'Meet locally',
    palette: 'green',
    hours: 10,
    icon: LuMapPin,
  },
] as const

/** Post-submit steps when the member is on a $99+ plan (auto-approved). */
export const AFTER_SUBMIT_STEPS_LIVE = [
  {
    n: 1,
    title: "You're live",
    desc: 'Members on the $99+ plan go live immediately. You can edit your profile anytime from Advisor profile.',
  },
  {
    n: 2,
    title: 'Watch Opportunities',
    desc: 'See board invites and open seats that match your skills, categories, and preferences.',
  },
  {
    n: 3,
    title: 'Engage founders',
    desc: 'Apply to seats and accept invites. Founders can message you in Inbox without a seat. You can still recruit your own board — this is one identity, not a mode switch.',
  },
]

/** Post-submit steps when the application is waiting for admin review. */
export const AFTER_SUBMIT_STEPS_PENDING = [
  {
    n: 1,
    title: 'In review',
    desc: 'Our team reviews unpaid applications before they go live. You will not appear in the directory until you are approved.',
  },
  {
    n: 2,
    title: 'Go live faster',
    desc: 'Members on the $99+ plan skip this queue and go live as soon as they submit.',
  },
  {
    n: 3,
    title: 'After approval',
    desc: 'Once approved, founders can find you in the directory and match you to open seats. You can still recruit your own board in the meantime.',
  },
]

/** @deprecated Prefer AFTER_SUBMIT_STEPS_LIVE / AFTER_SUBMIT_STEPS_PENDING. */
export const AFTER_SUBMIT_STEPS = AFTER_SUBMIT_STEPS_LIVE

/** @deprecated Prefer ADVISE_CATEGORY_NAMES — kept for any residual imports. */
export const ADVISE_TOPICS = ADVISE_CATEGORY_NAMES

/** @deprecated Prefer ENGAGE_OPTS. */
export const AVAILABILITY_OPTS = ENGAGE_OPTS.map(o => o.label)
